import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, getUser, saveToken, saveUser, removeToken, getDashboardPath } from '../lib/auth';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modulesState, setModulesState] = useState(null);

  const refreshModules = async () => {
    try {
      const res = await authApi.myModules();
      setModulesState(res);
    } catch {
      setModulesState({ enabled_modules: [], has_package: false, package_name: null });
    }
  };

  // Listen for 401 from any API call — clear user so ProtectedRoute redirects
  useEffect(() => {
    const handler = () => {
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      const savedUser = getUser();
      
      if (token && savedUser) {
        try {
          const currentUser = await authApi.me();
          setUser(currentUser);
          saveUser(currentUser);
          // Load modules for doctor/clinic users
          if (currentUser?.role === 'doctor') {
            await refreshModules();
          }
        } catch (error) {
          if (error?.status === 401) {
            // Token truly invalid/expired — clear session
            removeToken();
            setUser(null);
          } else {
            // Network error, 500, 502, timeout — keep cached session
            // Don't destroy a valid session over a transient failure
            console.warn('[Auth] me() failed with non-401, keeping cached session:', error?.message);
            setUser(savedUser);
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    // Defensive: production fetch wrapper in index.html can return `{}` on Safari if
    // the response body was double-consumed. Treat that as a hard failure so the
    // UI shows a real error instead of silently saving an undefined token.
    if (!response || !response.access_token || !response.user) {
      throw new Error('Giriş başarısız. Lütfen tekrar deneyin.');
    }
    saveToken(response.access_token);
    const userData = JSON.parse(JSON.stringify(response.user));
    saveUser(userData);
    setUser(userData);
    // Load modules for doctor users (otherwise ModuleGuard sees empty state on first render)
    if (userData?.role === 'doctor') {
      try { await refreshModules(); } catch { /* non-fatal */ }
    }
    return userData;
  };

  const register = async (email, password, name, extraData = {}) => {
    const response = await authApi.register({ 
      email, 
      password, 
      name,
      clinic_name: extraData.clinicName || 'Klinik',
      phone: extraData.phone || null,
      kvkk_consent: !!extraData.kvkkConsent,
    });
    if (!response || !response.access_token || !response.user) {
      throw new Error('Kayıt başarısız. Lütfen tekrar deneyin.');
    }
    saveToken(response.access_token);
    saveUser(response.user);
    setUser(response.user);
    return response.user;
  };

  const logout = () => {
    removeToken();
    setUser(null);
    window.location.href = '/login';
  };

  const updateUser = (updatedFields) => {
    setUser(prev => {
      const newUser = { ...prev, ...updatedFields };
      saveUser(newUser);
      return newUser;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        modulesState,
        refreshModules,
        login,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
